/**
 * JIRA/HubSpot Style Pipeline Kanban Board
 * Complete redesign with professional enterprise UI
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext } from '@hello-pangea/dnd';
import { Plus, RefreshCw, Database, TrendingUp, AlertCircle } from 'lucide-react';
import KanbanColumn from '../components/pipeline/KanbanColumn';
import KanbanFilters from '../components/pipeline/KanbanFilters';
import KanbanListView from '../components/pipeline/KanbanListView';
import KanbanSwimlanes from '../components/pipeline/KanbanSwimlanes';
import OpportunityDetailPanel from '../components/pipeline/OpportunityDetailPanel';
import CreateOpportunityDialog from '../components/dialogs/CreateOpportunityDialog';
import IHSCarlineBrowser from '../components/ihs/IHSCarlineBrowser';
import { Button } from '../components/ui/button';
import { 
  listOpportunities, 
  changeStage, 
  archiveOpportunity,
  getStageDisplayName, 
  formatCompactCurrency 
} from '../api/pipeline';
import * as workflowApi from '../api/workflows';

// Pipeline stages configuration
const STAGES = [
  { id: 'target', name: 'Target', color: 'blue' },
  { id: 'qualified', name: 'Qualified', color: 'purple' },
  { id: 'rfq', name: 'RFQ', color: 'indigo' },
  { id: 'quoted', name: 'Quoted', color: 'cyan' },
  { id: 'awarded', name: 'Awarded', color: 'emerald' },
  { id: 'sourced', name: 'Sourced', color: 'green' },
  { id: 'lost', name: 'Lost', color: 'slate' }
];

const PipelineBoard = () => {
  const navigate = useNavigate();
  // Data state
  const [opportunities, setOpportunities] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workflowCounts, setWorkflowCounts] = useState({});
  
  // UI state
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isIHSBrowserOpen, setIsIHSBrowserOpen] = useState(false);
  const [createStage, setCreateStage] = useState('target');
  
  // View state
  const [viewMode, setViewMode] = useState('kanban');
  const [swimlaneGrouping, setSwimlaneGrouping] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    oem: null,
    priority: null,
    region: null,
    assigned_to: null,
  });

  // Extract unique filter options from data
  const filterOptions = useMemo(() => {
    const allOpps = Object.values(opportunities).flat();
    const oems = [...new Set(allOpps.map(o => o.oem).filter(Boolean))].sort();
    const regions = [...new Set(allOpps.map(o => o.region).filter(Boolean))].sort();
    const assignees = [...new Set(
      allOpps
        .filter(o => o.assigned_to)
        .map(o => JSON.stringify({ id: o.assigned_to.id, name: o.assigned_to.name }))
    )].map(s => JSON.parse(s));
    
    return { oems, regions, assignees };
  }, [opportunities]);

  // Apply filters to opportunities
  const filteredOpportunities = useMemo(() => {
    const result = {};
    
    for (const [stage, opps] of Object.entries(opportunities)) {
      result[stage] = opps.filter(opp => {
        // Search filter
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matches = 
            opp.program_name?.toLowerCase().includes(searchLower) ||
            opp.oem?.toLowerCase().includes(searchLower) ||
            opp.platform_code?.toLowerCase().includes(searchLower);
          if (!matches) return false;
        }
        
        // OEM filter
        if (filters.oem && opp.oem !== filters.oem) return false;
        
        // Priority filter
        if (filters.priority && opp.priority !== filters.priority) return false;
        
        // Region filter
        if (filters.region && opp.region !== filters.region) return false;
        
        // Assignee filter
        if (filters.assigned_to && opp.assigned_to?.id !== filters.assigned_to) return false;
        
        return true;
      });
    }
    
    return result;
  }, [opportunities, filters]);

  // Fetch workflow counts for opportunities (non-blocking)
  const fetchWorkflowCounts = useCallback(async (oppIds) => {
    try {
      const counts = {};
      // Batch fetch - get all instances and count by entity_id
      const response = await workflowApi.listInstances({ status: 'active' });
      if (response.success && response.data) {
        response.data.forEach(instance => {
          if (instance.entity_type === 'opportunity' && instance.entity_id) {
            counts[instance.entity_id] = (counts[instance.entity_id] || 0) + 1;
          }
        });
      }
      setWorkflowCounts(counts);
    } catch (err) {
      // Non-critical, just log
    }
  }, []);

  // Calculate filtered stats
  const filteredStats = useMemo(() => {
    const allOpps = Object.values(filteredOpportunities).flat();
    const totalRevenue = allOpps.reduce((sum, o) => sum + (o.revenue_potential_cents || 0), 0);
    const weightedRevenue = allOpps.reduce((sum, o) => {
      const weight = (o.confidence_percent || 0) / 100;
      return sum + ((o.revenue_potential_cents || 0) * weight);
    }, 0);
    
    const byStage = {};
    for (const [stage, opps] of Object.entries(filteredOpportunities)) {
      byStage[stage] = {
        count: opps.length,
        revenue: opps.reduce((sum, o) => sum + (o.revenue_potential_cents || 0), 0)
      };
    }
    
    return {
      total_opportunities: allOpps.length,
      total_revenue_potential: totalRevenue,
      weighted_pipeline_value: weightedRevenue,
      by_stage: byStage
    };
  }, [filteredOpportunities]);

  // Load opportunities
  const loadOpportunities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listOpportunities();
      
      if (response.success) {
        setOpportunities(response.data.stages);
        setStats(response.data.stats);
        
        // Fetch workflow counts for all opportunities
        const allOpps = Object.values(response.data.stages).flat();
        const oppIds = allOpps.map(o => o.id);
        if (oppIds.length > 0) {
          fetchWorkflowCounts(oppIds);
        }
      } else {
        setError('Failed to load opportunities');
      }
    } catch (err) {
      setError(err.message || 'Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  }, [fetchWorkflowCounts]);

  useEffect(() => {
    loadOpportunities();
  }, [loadOpportunities]);

  // Handle drag end
  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const sourceStage = source.droppableId;
    const destStage = destination.droppableId;

    // Optimistic update
    const sourceOpps = Array.from(opportunities[sourceStage] || []);
    const destOpps = sourceStage === destStage 
      ? sourceOpps 
      : Array.from(opportunities[destStage] || []);
    
    const [movedOpp] = sourceOpps.splice(source.index, 1);
    
    if (sourceStage === destStage) {
      sourceOpps.splice(destination.index, 0, movedOpp);
      setOpportunities({
        ...opportunities,
        [sourceStage]: sourceOpps
      });
    } else {
      destOpps.splice(destination.index, 0, movedOpp);
      setOpportunities({
        ...opportunities,
        [sourceStage]: sourceOpps,
        [destStage]: destOpps
      });
    }

    // Update backend
    if (sourceStage !== destStage) {
      try {
        await changeStage(
          draggableId, 
          destStage, 
          `Moved from ${getStageDisplayName(sourceStage)} to ${getStageDisplayName(destStage)}`
        );
        loadOpportunities();
      } catch (err) {
        console.error('Error updating stage:', err);
        loadOpportunities(); // Revert on error
      }
    }
  };

  // Handle card click
  const handleCardClick = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setIsDetailPanelOpen(true);
  };

  // Handle stage change from card menu
  const handleStageChange = async (opportunityId, newStage) => {
    try {
      await changeStage(opportunityId, newStage);
      loadOpportunities();
    } catch (err) {
      console.error('Error changing stage:', err);
      alert('Failed to change stage');
    }
  };

  // Handle archive
  const handleArchive = async (opportunityId) => {
    if (window.confirm('Archive this opportunity?')) {
      try {
        await archiveOpportunity(opportunityId);
        loadOpportunities();
      } catch (err) {
        console.error('Error archiving:', err);
        alert('Failed to archive');
      }
    }
  };

  // Handle opening Planning tab for an opportunity
  const handleOpenPlanning = (opportunityId) => {
    // Find the opportunity and open detail panel on Planning tab
    const allOpps = Object.values(opportunities).flat();
    const opp = allOpps.find(o => o.id === opportunityId);
    if (opp) {
      setSelectedOpportunity(opp);
      setIsDetailPanelOpen(true);
      // The OpportunityDetailPanel will need to be updated to accept an initialTab prop
      // For now, we open the panel and users can click the Planning tab
    }
  };

  const handleOpenProgram = (opportunity) => {
    if (opportunity?.program_id) {
      navigate(`/programs/${opportunity.program_id}`);
    }
  };

  // Handle add opportunity to specific stage
  const handleAddOpportunity = (stageId) => {
    setCreateStage(stageId);
    setIsCreateDialogOpen(true);
  };

  // Loading state
  if (loading && Object.keys(opportunities).length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-slate-600">Loading pipeline...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={loadOpportunities}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-100">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4">
        {/* Top Row: Title + Actions */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              Acquisition Pipeline
            </h1>
            {/* Stats Summary */}
            <div className="flex items-center gap-4 mt-1 text-sm text-slate-600">
              <span className="font-medium">{filteredStats.total_opportunities} acquisition records</span>
              <span className="text-slate-400">|</span>
              <span className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                {formatCompactCurrency(filteredStats.total_revenue_potential)} Pipeline
              </span>
              <span className="text-slate-400">|</span>
              <span>{formatCompactCurrency(filteredStats.weighted_pipeline_value)} Weighted</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={loadOpportunities}
              disabled={loading}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsIHSBrowserOpen(true)}
              className="gap-2 bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
            >
              <Database className="w-4 h-4" />
              Browse Market Forecasts
            </Button>
            <Button 
              onClick={() => handleAddOpportunity('target')}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              New Acquisition
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        <KanbanFilters
          filters={filters}
          onFilterChange={setFilters}
          availableOEMs={filterOptions.oems}
          availableRegions={filterOptions.regions}
          availableAssignees={filterOptions.assignees}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          swimlaneGrouping={swimlaneGrouping}
          onSwimlaneGroupingChange={setSwimlaneGrouping}
        />
      </div>

      {/* Main Content Area - Switches between views */}
      <div className="flex-1 overflow-auto">
        {viewMode === 'list' ? (
          /* List View */
          <div className="p-4">
            <KanbanListView
              opportunities={filteredOpportunities}
              onCardClick={handleCardClick}
              onStageChange={handleStageChange}
              onArchive={handleArchive}
            />
          </div>
        ) : swimlaneGrouping ? (
          /* Swimlanes View */
          <div className="p-4">
            <KanbanSwimlanes
              opportunities={filteredOpportunities}
              groupBy={swimlaneGrouping}
              onCardClick={handleCardClick}
              onStageChange={handleStageChange}
              onArchive={handleArchive}
              onDragEnd={onDragEnd}
            />
          </div>
        ) : (
          /* Default Kanban Board */
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="flex gap-4 p-4 h-full min-w-max">
              {STAGES.map((stage) => (
                <KanbanColumn
                  key={stage.id}
                  stage={stage}
                  opportunities={filteredOpportunities[stage.id] || []}
                  stats={filteredStats.by_stage?.[stage.id]}
                  onCardClick={handleCardClick}
                  onStageChange={handleStageChange}
                  onArchive={handleArchive}
                  onAddOpportunity={handleAddOpportunity}
                  onOpenPlanning={handleOpenPlanning}
                  onOpenProgram={handleOpenProgram}
                  workflowCounts={workflowCounts}
                />
              ))}
            </div>
          </DragDropContext>
        )}
      </div>

      {/* Detail Panel (Slide-out) */}
      <OpportunityDetailPanel
        opportunityId={selectedOpportunity?.id}
        isOpen={isDetailPanelOpen}
        onClose={() => {
          setIsDetailPanelOpen(false);
          setSelectedOpportunity(null);
        }}
        onUpdate={loadOpportunities}
      />

      {/* Create Opportunity Dialog */}
      <CreateOpportunityDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        defaultStage={createStage}
        onSuccess={() => {
          loadOpportunities();
        }}
      />

      {/* Market Forecast Browser */}
      {isIHSBrowserOpen && (
        <IHSCarlineBrowser
          opportunityId={null}
          onClose={() => setIsIHSBrowserOpen(false)}
          onCreateOpportunity={() => {
            setIsIHSBrowserOpen(false);
            loadOpportunities();
          }}
        />
      )}
    </div>
  );
};

export default PipelineBoard;
